import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	Input,
	OnDestroy,
	OnInit,
	ViewEncapsulation,
} from "@angular/core";
import { UntypedFormBuilder, Validators } from "@angular/forms";
import { select, Store } from "@ngrx/store";
import { AlertButtonColor } from "@obg/mat.ui.core";
import {
	CommonState,
	MULTI_SESSION_LIMIT_MESSAGE_CONTEXT,
	MultiSessionLimit,
	MultiSessionLimitAction,
	MultiSessionLimitConfigService,
	MultiSessionLimitResponse,
	MultiSessionLimitSelector,
} from "@obg/ngx.common";
import {
	AbstractTranslatableAlertService,
	AbstractTranslatableSnackBarService,
	Jurisdiction,
	MessageService,
	MessageType,
} from "@obg/ngx.core";
import { TranslationGroup } from "@obg/ngx.core.content";
import { sessionLimitAmountValidator } from "@obg/ngx.ui.common";
import { pickBy } from "lodash-es";
import { merge, Subscription } from "rxjs";
import { take, tap } from "rxjs/operators";

import { ResponsibleGamingFormService } from "../../containers/responsible-gaming-form.service";

import {
	ArgentineSessionLimitFormGroup,
	ArgentineSessionLimitPeriod,
	ArgentineSessionLimitPeriodTranslationsEnum,
	ArgentineSessionLimitSuggestionAmounts,
	ArgentineSessionLimitTemplate,
} from "./argentine-session-limit.container.model";

const TRANSLATION_PREFIX = "multi-session-limit";

@Component({
	selector: "obg-m-argentine-session-limit-container",
	templateUrl: "./argentine-session-limit.container.html",
	styleUrls: ["./argentine-session-limit.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class ArgentineSessionLimitContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-argentine-session-limit") hostClass = true;

	@Input() jurisdiction: Jurisdiction | undefined;

	readonly sessionLimitPeriodControls = ArgentineSessionLimitPeriod;

	isSettingBusy = false;
	currentSessionLimit: ArgentineSessionLimitTemplate | undefined;
	upcomingSessionLimit: ArgentineSessionLimitTemplate | undefined;

	formGroup: ArgentineSessionLimitFormGroup = this.formBuilder.group({
		sessionLimitAmount: this.formBuilder.control("", [sessionLimitAmountValidator]),
		sessionLimitPeriod: this.formBuilder.control(ArgentineSessionLimitPeriod.Daily, [Validators.required]),
	}) as ArgentineSessionLimitFormGroup;
	isSessionLimitEdited = false;
	placeholder = "24";
	private data$$: Subscription | undefined;
	private isFormDirty$$: Subscription | undefined;

	constructor(
		private multiSessionLimitSelector: MultiSessionLimitSelector,
		private multiSessionLimitAction: MultiSessionLimitAction,
		private formBuilder: UntypedFormBuilder,
		private store: Store<CommonState>,
		private snackBarService: AbstractTranslatableSnackBarService,
		private alertService: AbstractTranslatableAlertService,
		private changeDetector: ChangeDetectorRef,
		private messageService: MessageService,
		private multiSessionLimitConfigService: MultiSessionLimitConfigService,
		private responsibleGamingFormService: ResponsibleGamingFormService
	) {}

	ngOnInit() {
		this.store.dispatch(this.multiSessionLimitAction.get());

		const sessionLimits$ = this.store.pipe(
			select(this.multiSessionLimitSelector.get()),
			tap((sessionLimit) => {
				this.currentSessionLimit = undefined;
				this.upcomingSessionLimit = undefined;
				if (sessionLimit?.current) {
					this.currentSessionLimit = this.mapSessionLimits(sessionLimit.current);
				}

				if (sessionLimit?.upcoming) {
					this.upcomingSessionLimit = this.mapSessionLimits(sessionLimit.upcoming);
				}
			})
		);

		const sessionLimitUpdate$ = this.messageService
			.consume$({
				context: MULTI_SESSION_LIMIT_MESSAGE_CONTEXT.update,
			})
			.pipe(
				tap((message) => {
					this.isSettingBusy = false;
					this.formGroup.setValue({
						sessionLimitAmount: "",
						sessionLimitPeriod: ArgentineSessionLimitPeriod.Daily,
					});
					if (message[0].type === MessageType.Success) {
						this.updateSuccess("update_success");
						return;
					}
					this.updateFail();
				})
			);

		const sessionLimitPeriodControlChanges$ = this.formGroup.controls.sessionLimitPeriod.valueChanges.pipe(
			tap((value: ArgentineSessionLimitPeriod) => {
				this.formGroup.controls.sessionLimitAmount.reset();
				this.placeholder =
					ArgentineSessionLimitSuggestionAmounts[
						ArgentineSessionLimitPeriod[value] as keyof typeof ArgentineSessionLimitSuggestionAmounts
					].toString();
			})
		);

		const isBusy$ = this.store.pipe(
			select(this.multiSessionLimitSelector.isBusy()),
			tap((isBusy) => (this.isSettingBusy = isBusy))
		);

		const resetForm$ = this.responsibleGamingFormService.resetForm$.pipe(
			tap(() => {
				this.formGroup?.reset({
					sessionLimitAmount: "",
					sessionLimitPeriod: ArgentineSessionLimitPeriod.Daily,
				});
			})
		);

		this.data$$ = merge(
			sessionLimits$,
			sessionLimitUpdate$,
			sessionLimitPeriodControlChanges$,
			isBusy$,
			resetForm$
		).subscribe(() => this.changeDetector.markForCheck());

		this.isFormDirty$$ = this.formGroup.valueChanges.subscribe(() =>
			this.responsibleGamingFormService.sendIsFormDirty(this.formGroup.dirty)
		);
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
		this.isFormDirty$$?.unsubscribe();
	}

	editSessionLimit() {
		this.isSessionLimitEdited = true;
	}

	submitSessionLimit() {
		const messageKey = this.multiSessionLimitConfigService.getMultiSessionLimitSuffixed()
			? `${TRANSLATION_PREFIX}.confirmation-caption.${ArgentineSessionLimitPeriod[
					this.formGroup.controls.sessionLimitPeriod.value
				].toLowerCase()}`
			: `${TRANSLATION_PREFIX}.confirmation-caption`;

		this.alertService.open(
			{
				id: this.getAlertId(this.jurisdiction),
				title: `${TRANSLATION_PREFIX}.confirmation-title`,
				message: messageKey,
				hideClose: true,
				dialogConfig: {
					disableClose: true,
				},
				mustBeInteracted: true,
				buttons: [
					{
						text: "general.cancel",
						color: AlertButtonColor.Primary,
					},
					{
						text: "general.confirm",
						handler: () => {
							this.store.dispatch(
								this.multiSessionLimitAction.update(
									this.getMultiSessionLimitValues(
										this.formGroup.controls.sessionLimitAmount.value,
										this.formGroup.controls.sessionLimitPeriod.value as ArgentineSessionLimitPeriod
									)
								)
							);
							this.isSettingBusy = true;
							this.changeDetector.markForCheck();
						},
					},
				],
			},
			[TranslationGroup.Common]
		);
	}

	private getAlertId(jurisdiction: Jurisdiction | undefined): string {
		switch (jurisdiction) {
			case Jurisdiction.LPCSE:
				return "LPCSE_SESSION_LIMIT_ALERT_ID";
			case Jurisdiction.Lotba:
				return "LOTBA_SESSION_LIMIT_ALERT_ID";
			case Jurisdiction.Iplyc:
				return "IPLYC_SESSION_LIMIT_ALERT_ID";
			default:
				return "SESSION_LIMIT_ALERT_ID";
		}
	}

	private updateFail() {
		this.snackBarService
			.openSnackBar$(
				{
					message: "general.e",
					duration: 4000,
					action: "general.close",
					type: MessageType.Error,
				},
				[TranslationGroup.Common]
			)
			.pipe(take(1))
			// eslint-disable-next-line rxjs/no-ignored-subscription
			.subscribe();
	}

	private updateSuccess(suffix: string) {
		this.snackBarService
			.openSnackBar$(
				{
					message: `${TRANSLATION_PREFIX}.m_multi_session_limit_${suffix}`,
					duration: 4000,
					action: "general.close",
					type: MessageType.Success,
				},
				[TranslationGroup.Common]
			)
			.pipe(take(1))
			// eslint-disable-next-line rxjs/no-ignored-subscription
			.subscribe();
	}

	private mapSessionLimits(sessionLimit: MultiSessionLimitResponse) {
		const sessionLimitPeriod: MultiSessionLimit = pickBy(
			sessionLimit,
			(value, key: keyof MultiSessionLimitResponse) => value !== 0 && key !== "activeFrom"
		);
		return { ...this.getSessionLimitPeriodTranslation(sessionLimitPeriod), activeFrom: sessionLimit.activeFrom };
	}

	private getSessionLimitPeriodTranslation(sessionLimitPeriod: MultiSessionLimit) {
		const key = Object.keys(sessionLimitPeriod)[0] as ArgentineSessionLimitPeriodTranslationsEnum;
		const period = Object.keys(ArgentineSessionLimitPeriodTranslationsEnum)
			.find(
				(x: keyof typeof ArgentineSessionLimitPeriodTranslationsEnum) =>
					ArgentineSessionLimitPeriodTranslationsEnum[x] === key
			)!
			.toLowerCase();
		return { limitValue: sessionLimitPeriod[key]!, limitPeriod: period };
	}

	private getMultiSessionLimitValues(sessionLimitAmount: number, sessionLimitPeriod: ArgentineSessionLimitPeriod) {
		const periodName = ArgentineSessionLimitPeriod[
			sessionLimitPeriod
		] as keyof typeof ArgentineSessionLimitPeriodTranslationsEnum;
		const period = ArgentineSessionLimitPeriodTranslationsEnum[periodName];
		return { [period]: sessionLimitAmount };
	}
}
